import Minilog from 'minilog';

import {mergeObjects} from './core/objects';
import {getStackTrace} from './core/exceptions';
import {addErrorMessage} from './feedback';
import {createMergeAction} from './base';
import {V3Resource, V2Resource, V2ListModel, RequestPromise} from './services';
import {V2Region} from './geo';
import * as Locale from './locale';
import {CABINETS_ROUTE_LISTING} from './routes/main';
import {setWindowLocation} from './core/browser';

let logger = Minilog('frontend.models.premises');

export interface V2Premises {
    id: number;
    name: string;
    street_address: string;
    extended_Address: string;
    locality: string;
    postal_code: string;
    region: V2Region;
    subscribed: boolean;
    dashboard_display: boolean;
    added: boolean;
    slug: string;
    resource_uri: string;
    uncleared_alarms: number;
    units_offline: number;
    total_units: number;
    num_of_units: number;
}

export interface PremisesPatch {
    subscribed: boolean;
    dashboard_display: boolean;
}

export interface AreaFilter {
    region?: string;
    region__country?: string;
}

export interface PremisesParameters extends AreaFilter {
    page?: number;
    premises?: string;
    disable_alarm_count?: boolean;
    enable_counts?: boolean;
}

export interface CreatePremisesParameters {
    name: string;
    gateway_id: string;
    address1: string;
    address2?: string;
    postal_code: string;
    region: number;
    subscribe?: boolean;
    email?: string;
    mobile?: string;
}

/**
 * For handling all premises v2 API calls
 */
class PremisesV2Resource extends V2Resource {
    constructor() {
        super(V2Resource.PREMISES);
    }

    list(params?: PremisesParameters) : RequestPromise<V2ListModel<V2Premises>> {
        // inject standard params that v2 API expects
        let allParams = mergeObjects(
            {display: true, limit: 15, offset: 0}, {}, params
        );
        return super.list(allParams);
    }
}

/**
 * For handling all premises v3 API calls
 */
class PremisesV3Resource extends V3Resource {
    constructor() {
        super(V3Resource.PREMISES);
    }
}

/**
 * Global premises v3 API instance
 *
 * @type {PremisesV3Resource}
 */
export let PremisesV3API: PremisesV3Resource = new PremisesV3Resource();
/**
 * Global premises v2 API instance
 *
 * @type {PremisesV2Resource}
 */
export let PremisesV2API: PremisesV2Resource = new PremisesV2Resource();

export function redirectToCabinetsBySlug(slug: string) {
    // TODO: use proper React route once cabinets listing page is Reactified
    let uri = `${CABINETS_ROUTE_LISTING}?store_slug=${slug}`;
    setWindowLocation(uri);
}

/**
 * Redits the current page to the cabinets listing for the premises by id
 */
export function redirectToCabinetsByPremisesId(premisesId: number) {
    let uri = `${CABINETS_ROUTE_LISTING}?store_id=${premisesId}`;
    setWindowLocation(uri);
}

/**
 * Redirects the current page to the cabinets listing page for the premises
 * @param premises
 */
export function redirectToCabinets(premises: V2Premises) {
    redirectToCabinetsBySlug(premises.slug);
}

/**
 * Merge: enable loading progress for premises page
 */
function enablePremisesLoad() {
    return createMergeAction(
        'premises_load',
        {ui: {premises: {loading: true}}}
    );
}

/**
 * Merge: disable loading progress for premises page
 */
function disablePremisesLoad() {
    return createMergeAction(
        'premises_loaded',
        {ui: {premises: {loading: false}}}
    );
}

/**
 * Merge: set data for Premises page
 */
function setPremises(premisesData: any) {
    return createMergeAction(
        'premises_set',
        {data: {premises: premisesData}},
        {array_replace: true}
    );
}

/**
 * Thunk: load the latest set of premises from the API, optionally filtered
 * by the given filter data
 */
export function getPremises(parameters?: PremisesParameters) {
    return function (dispatch) {
        dispatch(enablePremisesLoad());
        let params = parameters || {};
        params.enable_counts = true;
        // todo: filter by given filter
        return PremisesV2API.list(params).then(function(response) {
            dispatch(setPremises(response.data));
            dispatch(disablePremisesLoad());
        }).catch(function(error) {
            logger.error(`Failed to load premises: ${error}`);
            // todo: workout some way of integrating stack trace logging
            // automatically
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_PREMISES));
            dispatch(disablePremisesLoad());
        });
    };
}

/**
 * Thunk: loads all premises from the API, optionally filtered by the given
 * filter data
 */
export function getAllPremises(parameters?: PremisesParameters) {
    return function (dispatch) {
        dispatch(enablePremisesLoad());
        let params = parameters || {};
        return PremisesV2API.listAll(params).then(function(response) {
            dispatch(setPremises({
                results: response.data, count: response.data.length
            }));

            dispatch(disablePremisesLoad());
        }).catch(function(error) {
            logger.error(`Failed to load premises: ${error}`);
            // todo: workout some way of integrating stack trace logging
            // automatically
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_PREMISES));
            dispatch(disablePremisesLoad());
        });
    };
}

function setPremisesIsSubscribing(id: number, isSubscribing: boolean) {
    return createMergeAction(
        'premises_subscribing',
        {
            ui: {premises: {[id]: {isSubscribing: isSubscribing}}}
        }
    );
}

export const PREMISES_SET_SUBSCRIBE = 'premises_set_subscribe';
function setPremisesSubscribed(id, subscribed) {
    return { type: PREMISES_SET_SUBSCRIBE, id, subscribed };
}

export function subscribeToPremises(id: number, subscribe: boolean) {
    return function (dispatch) {
        dispatch(setPremisesIsSubscribing(id, true));
        return PremisesV2API.update<PremisesPatch>(id, {subscribed: subscribe})
            .then(r => dispatch(setPremisesSubscribed(id, r.data.subscribed)))
            .catch(error => {
                logger.error(`Failed to subscribe to premises: ${error}`);
                logger.debug(getStackTrace(error));
                dispatch(addErrorMessage(Locale.TEXT_ERROR_PREMISES_SUBSCRIBE));
                dispatch(setPremisesIsSubscribing(id, false));
            });
    };
}

function setPremisesDashboardDisplayChanging(id, value: boolean) {
    return createMergeAction(
        'premises_dashboard_display_updating',
        {
            ui: {premises: {[id]: {settingDashboardDisplay: value}}}
        }
    );
}

export const PREMISES_SET_DASHBOARD_DISPLAY = 'premises_set_dashboard_display';
function setPremisesDashboardDisplay(id, dashboard_display: boolean) {
    return { type: PREMISES_SET_DASHBOARD_DISPLAY, id, dashboard_display };
}

export function updateDashboardDisplay(id, showOnDashboard) {
    return function (dispatch) {
        dispatch(setPremisesDashboardDisplayChanging(id, true));
        return PremisesV2API.update<PremisesPatch>(id, {dashboard_display: showOnDashboard})
            .then(r => dispatch(setPremisesDashboardDisplay(id, r.data.dashboard_display)))
            .catch(error => {
                logger.error(`Failed to change dashboard display for premises: ${error}`);
                logger.debug(getStackTrace(error));
                dispatch(addErrorMessage(Locale.TEXT_ERROR_PREMISES_SUBSCRIBE));
                dispatch(setPremisesDashboardDisplayChanging(id, false));
            });
    };
}

export const PREMISES_SET_COUNTRY_FILTER = 'premises_set_country_filter';
export function setCountryFilter(id: number) {
    return {type: PREMISES_SET_COUNTRY_FILTER, id};
}

export const PREMISES_SET_REGION_FILTER = 'premises_set_region_filter';
export function setRegionFilter(id: number) {
    return {type: PREMISES_SET_REGION_FILTER, id};
}

export function filterByRegion(id: number) {
    return dispatch => {
        dispatch(setRegionFilter(id));
        dispatch(getPremises());
    };
}

export function filterByCountry(id: number) {
    return dispatch => {
        dispatch(setCountryFilter(id));
        dispatch(getPremises());
    };
}
